export interface BoosterDefinition {
  id: string;
  displayName: string;
  description: string;
  effectType: string;
  multiplier?: number;
  durationMinutes?: number;
  requiresPlan?: "free" | "pro" | "ultra";
}

/**
 * Shared source of truth for booster metadata in BizNearYou.
 */
export const BOOSTER_DEFINITIONS: Record<string, BoosterDefinition> = {
  quest_xp_boost: {
    id: "quest_xp_boost",
    displayName: "Quest XP Boost",
    description: "Earn 2× XP from eligible quest completions while active.",
    effectType: "quest_xp_multiplier",
    multiplier: 2,
    durationMinutes: 30,
    requiresPlan: "free"
  },
  search_xp_boost: {
    id: "search_xp_boost",
    displayName: "Search XP Boost",
    description: "Earn an additional 10 XP when completing eligible business-search quests today.",
    effectType: "search_xp_addition",
    multiplier: 1.5,
    durationMinutes: 1440,
    requiresPlan: "free"
  },
  daily_quest_boost: {
    id: "daily_quest_boost",
    displayName: "Daily Quest Boost",
    description: "Earn 2× XP from completed daily quests for 24 hours.",
    effectType: "daily_xp_multiplier",
    multiplier: 2,
    durationMinutes: 1440,
    requiresPlan: "free"
  },
  pro_quest_reward_boost: {
    id: "pro_quest_reward_boost",
    displayName: "Pro Quest Reward Boost",
    description: "Pro members earn 25% additional XP from eligible Pro quests and daily challenges.",
    effectType: "pro_quest_xp_multiplier",
    multiplier: 1.25,
    durationMinutes: 1440,
    requiresPlan: "pro"
  }
};

/**
 * Returns user-facing booster info given user subscription tier and active duration.
 * 
 * Legacy field note:
 * Database columns like `pro_booster_until` and `pro_booster_minutes` store temporary
 * booster expiration dates and durations for backward compatibility with Supabase schema.
 * However, for non-Pro users, this represents a free "Quest XP Boost" rather than a Pro Booster.
 */
export function getActiveBoosterDisplay(isProUser: boolean, timeRemaining: string | null) {
  const baseDef = isProUser ? BOOSTER_DEFINITIONS.pro_quest_reward_boost : BOOSTER_DEFINITIONS.quest_xp_boost;
  
  return {
    ...baseDef,
    isActive: !!timeRemaining,
    timeRemaining: timeRemaining || "Inactive",
    statusText: timeRemaining ? "Active" : "Inactive",
    accessText: isProUser ? "Pro Plan Required" : "Available to All Users",
    effectText: isProUser ? "+25% Quest XP" : "2× Quest XP"
  };
}
